import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import { useDrawerContext } from '@hooks';
import { useTheme, useMediaQuery } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useStyles } from './styles';
import ChevronRightIcon from '@components/Icons/ChevronRight';
import FoodDeliveryServiceIcon from '@components/Icons/FoodDeliveryServiceIcon';
import MenuIcon from '@components/Icons/Menu';
import SettingsIcon from '@components/Icons/SettingsIcon';
import LogoutIcon from '@components/Icons/Logout';

const drawerWidth = 300;

const openedMixin = theme => ({
  width: drawerWidth,
  height: 'auto',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: 'transparent',
  borderRight: 'none',
  paddingTop: '70px',
  paddingLeft: '15px',
  paddingRight: '18px',
  color: theme.palette.common.white,
});

const closedMixin = theme => ({
  width: `calc(${theme.spacing(11.875)} + 1px)`,
  height: 'auto',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: 'transparent',
  borderRight: 'none',
  paddingTop: '70px',
  paddingLeft: '15px',
  paddingRight: '18px',
  color: theme.palette.common.white,
});

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  position: 'relative',
  ...(open && {
    ...openedMixin(theme),
    [`& .${drawerClasses.paper}`]: openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    [`& .${drawerClasses.paper}`]: closedMixin(theme),
  }),
}));

const ArrowToggleDrawer = () => {
  const theme = useTheme();
  const { openDrawer, toggleDrawer } = useDrawerContext();

  return (
    <Box
      elementtype="button"
      onClick={() => toggleDrawer(!openDrawer)}
      sx={{
        width: 18,
        height: 46,
        position: 'absolute',
        top: '50%',
        right: 0,
        cursor: 'pointer',
        borderTopRightRadius: theme.shape.borderRadius * 2.5,
        borderBottomRightRadius: theme.shape.borderRadius * 2.5,
        backgroundColor: theme.palette.dark.midnightBlue,
      }}
    >
      <Box
        sx={{
          marginTop: '15px',
          marginLeft: '3px',
        }}
      >
        <ChevronRightIcon />
      </Box>
    </Box>
  );
};
const ItemsList = ({ items, isOther }) => {
  const { openDrawer } = useDrawerContext();
  const theme = useTheme();
  const { pathname } = useLocation();
  return (
    <List
      sx={{
        p: 1.25,
        ...(!isOther
          ? {
              borderTopLeftRadius: theme.shape.borderRadius * 2.5,
              borderTopRightRadius: theme.shape.borderRadius * 2.5,
              backgroundColor: theme.palette.dark.midnightBlue,
            }
          : {
              borderBottomLeftRadius: theme.shape.borderRadius * 2.5,
              borderBottomRightRadius: theme.shape.borderRadius * 2.5,
              backgroundColor: theme.palette.dark.darkGrayish,
            }),
      }}
    >
      {items.map(({ name, Icon, path }, index) => {
        const pathMatched = path === pathname;
        return (
          <ListItem
            key={index}
            disablePadding
            sx={{
              display: 'block',
              py: 0.25,
              my: 1,
              ...(pathMatched && {
                background: theme.colors.orange.darkOrange,
                borderRadius: 2.5,
              }),
              '& .MuiTouchRipple-root': { borderRadius: 2.5 },
            }}
          >
            <ListItemButton
              sx={{
                justifyContent: openDrawer ? 'initial' : 'center',
              }}
              component={Link}
              to={path}
            >
              <ListItemIcon
                sx={{
                  width: 24,
                  height: 24,
                  minWidth: 'auto',
                  mr: openDrawer ? 2.375 : 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {Icon}
              </ListItemIcon>
              {openDrawer && (
                <ListItemText
                  primary={name}
                  sx={{
                    fontSize: theme.typography.fontSize,
                    fontWeight: theme.typography.fontWeightMedium,
                  }}
                  disableTypography
                />
              )}
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

export default function ClippedDrawer() {
  const classes = useStyles();
  const { openDrawer, toggleDrawer } = useDrawerContext();
  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down('sm'));
  const stringifyPlaceData = localStorage.getItem('place');
  const parsedPlaceData = JSON.parse(stringifyPlaceData);

  const items = [
    // {
    //   name: 'Dashboard',
    //   Icon: <DashboardIcon />,
    //   path: '/',
    // },
    {
      name: 'Service',
      Icon: <FoodDeliveryServiceIcon />,
      path: `/service/${parsedPlaceData.placeId}/orders`,
    },
    // {
    //   name: 'Tables',
    //   Icon: <DiningTableIcon />,
    //   path: '/',
    // },
    // {
    //   name: 'Staff',
    //   Icon: <StaffProfileIcon />,
    //   path: '/',
    // },
    // {
    //   name: 'Products',
    //   Icon: <FastFoodIcon />,
    //   path: '/',
    // },
    {
      name: 'Menu',
      Icon: <MenuIcon />,
      path: '/menu-management',
    },
    // {
    //   name: 'Product Groups & Views',
    //   Icon: <GroupViewIcon />,
    //   path: '/',
    // },
    // {
    //   name: 'Product Modifiers',
    //   Icon: <ProductModifiersIcon />,
    //   path: '/',
    // },
    // {
    //   name: 'Sales',
    //   Icon: <SalesIcon />,
    //   path: '/',
    // },
  ];
  const otherItems = [
    {
      name: 'Settings',
      Icon: <SettingsIcon />,
      path: '/account-settings-screen',
    },
    {
      name: 'Logout',
      Icon: <LogoutIcon />,
      path: '/logout',
    },
  ];

  return (
    <Box>
      {!matchesSM ? (
        <StyledDrawer variant="permanent" open={openDrawer}>
          <ArrowToggleDrawer />
          <ItemsList items={items} isOther={false} />
          <ItemsList items={otherItems} isOther />
        </StyledDrawer>
      ) : (
        <Drawer
          // container={container}
          variant="temporary"
          anchor={'left'}
          open={openDrawer}
          onClose={toggleDrawer}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <List>
            {items.map((Item, index) => (
              <ListItem
                component={Link}
                to={Item.path}
                button
                className={`${classes.drawerItem} ${index === 0 &&
                  classes.selected} `}
                key={Item.name}
              >
                <ListItemIcon className={classes.drawerItem}>
                  {' '}
                  <Item.Icon />{' '}
                </ListItemIcon>
                <ListItemText primary={Item.name} />
              </ListItem>
            ))}
          </List>
          <List>
            {otherItems.map((Item, index) => (
              <ListItem
                component={Link}
                to={Item.path}
                button
                className={`${classes.drawerItem}`}
                key={Item.name}
              >
                <ListItemIcon className={classes.drawerItem}>
                  <Item.Icon />
                </ListItemIcon>
                <ListItemText primary={Item.name} />
              </ListItem>
            ))}
          </List>
        </Drawer>
      )}
    </Box>
  );
}
